import type { TransactionRequest } from "viem";

import type { Address } from "./address";
import { type CallQueryType, EthCall } from "./ethCall";
import type { Network } from "./network";
import {
  BalanceMethod,
  type GetBalanceQuery,
  type GetEstimateGas,
  type GetStorageAtQuery,
  type GetTransactionByHashQuery,
  type GetTransactionReceiptQuery,
  type RawRPCQuery,
  StorageAtMethod,
  latestBlock
} from "./types";

export const BalanceQuery = (
  addr: Address<Network>,
  options: { expiry?: number; blockNumber: string } = {
    blockNumber: latestBlock
  }
): GetBalanceQuery => ({
  method: BalanceMethod,
  params: [addr.toString(), options.blockNumber]
});

export const TransactionReceiptQuery = (
  tx: `0x${string}`
): GetTransactionReceiptQuery => ({
  method: "eth_getTransactionReceipt",
  params: [tx]
});

export const TransactionByHashQuery = (
  tx: `0x${string}`
): GetTransactionByHashQuery => ({
  method: "eth_getTransactionByHash",
  params: [tx]
});

export const EstimateGasQuery = (tx: TransactionRequest): GetEstimateGas => ({
  method: "eth_estimateGas",
  params: [tx]
});

export const CallQuery = (
  to: Address<Network>,
  data: string,
  options: { expiry?: number; blockNumber?: string } = {
    blockNumber: latestBlock
  }
): CallQueryType => ({
  method: EthCall,
  params: [{ to: to.toString(), data }, latestBlock],
  expiry: options?.expiry
});

export const StorageAtQuery = (
  addr: Address<Network>,
  key: `0x${string}`,
  options: { expiry?: number; blockNumber: string } = {
    blockNumber: latestBlock
  }
): GetStorageAtQuery => ({
  method: StorageAtMethod,
  params: [addr.toString(), key, options.blockNumber]
});

// We remove variable parameters unrelated to the query itself
// (block number, expiry) from each query
export const RawRPCQueryForHash = (
  q: RawRPCQuery,
  options: { removeExtraParams?: boolean } = { removeExtraParams: true }
) => {
  switch (q.method) {
    case EthCall:
      // remove expiry and latest block
      return {
        method: EthCall,
        params: options.removeExtraParams ? q.params[0] : q.params
      };
    case BalanceMethod:
      return {
        method: BalanceMethod,
        params: options.removeExtraParams ? q.params[0] : q.params
      };
    default:
      return q;
  }
};

export const isRPCQuery = (query: unknown): query is RawRPCQuery =>
  typeof query === "object" &&
  query !== null &&
  "method" in query &&
  "params" in query;
